import { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { useLocation, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '../components/ui/tabs';
import { Clock, AlertCircle, CheckCircle2, Loader2, Eye, EyeOff } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

function formatApiErrorDetail(detail) {
  if (detail == null) return 'Ocurrió un error. Probá nuevamente.';
  if (typeof detail === 'string') return detail;
  if (Array.isArray(detail)) {
    return detail
      .map((e) => (e && typeof e.msg === 'string' ? e.msg : JSON.stringify(e)))
      .filter(Boolean)
      .join(' ');
  }
  if (detail && typeof detail.msg === 'string') return detail.msg;
  return String(detail);
}

function GoogleIcon() {
  return (
    <svg className="w-5 h-5 mr-3" viewBox="0 0 24 24">
      <path fill="currentColor" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
      <path fill="currentColor" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
      <path fill="currentColor" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
      <path fill="currentColor" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
    </svg>
  );
}

function Login() {
  const location = useLocation();
  const navigate = useNavigate();
  const pendingApprovalMsg = location.state?.pendingApproval;

  const [tab, setTab] = useState('email'); // 'email' | 'google'
  // Versión: única fuente de verdad = /docs/docs.json (misma que muestra el interior de la app)
  const [appVersion, setAppVersion] = useState('');
  useEffect(() => {
    fetch('/docs/docs.json')
      .then(r => r.json())
      .then(d => setAppVersion(`${d.version} · ${d.date}`))
      .catch(() => setAppVersion(''));
  }, []);
  const [mode, setMode] = useState('login'); // 'login' | 'register'
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [name, setName] = useState('');
  const [showPwd, setShowPwd] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [pending, setPending] = useState(pendingApprovalMsg || '');

  const handleGoogleLogin = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + '/';
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  const submit = async (e) => {
    e.preventDefault();
    setError('');
    setPending('');
    setLoading(true);
    try {
      if (mode === 'login') {
        await axios.post(
          `${BACKEND_URL}/api/auth/login`,
          { email: email.trim(), password },
          { withCredentials: true }
        );
        // Recargar la app para que el AuthProvider lea la cookie
        window.location.href = '/';
      } else {
        await axios.post(
          `${BACKEND_URL}/api/auth/register`,
          { email: email.trim(), password, name: name.trim() },
          { withCredentials: true }
        );
        // Si el backend devuelve 200 (caso super-admin) → login automático
        window.location.href = '/';
      }
    } catch (err) {
      const status = err.response?.status;
      const msg = formatApiErrorDetail(err.response?.data?.detail) || err.message;
      // 403 en register = cuenta creada pero pendiente de aprobación. Es un caso "exitoso".
      if (mode === 'register' && status === 403) {
        setPending(msg);
        setMode('login');
        setPassword('');
      } else {
        setError(msg);
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center relative overflow-hidden bg-gradient-to-br from-zinc-50 to-zinc-100 dark:from-zinc-950 dark:to-zinc-900">
      <div className="absolute inset-0 overflow-hidden">
        <div className="absolute -top-40 -right-40 w-80 h-80 bg-blue-600/10 rounded-full blur-3xl"></div>
        <div className="absolute -bottom-40 -left-40 w-80 h-80 bg-blue-600/10 rounded-full blur-3xl"></div>
      </div>

      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
        className="relative z-10 w-full max-w-md mx-4"
      >
        <div className="bg-card border border-border shadow-2xl rounded-lg p-8 backdrop-blur-sm">
          {/* Logo */}
          <div className="text-center mb-6">
            <div className="inline-flex items-center justify-center mb-4">
              <img src="/logo-hogar.png" alt="Hogar Cuatro Estaciones" className="h-24" />
            </div>
            <h1 className="text-3xl font-bold tracking-tight text-foreground mb-2">Hogar Gestión</h1>
            <p className="text-sm text-muted-foreground">
              Sistema de gestión integral - Hogar Cuatro Estaciones
            </p>
          </div>

          {/* Pending approval banner */}
          {pending && (
            <div
              data-testid="pending-approval-banner"
              className="mb-5 rounded-lg border border-amber-300 bg-amber-50 dark:bg-amber-950/30 dark:border-amber-800 p-4 text-center"
            >
              <div className="flex items-center justify-center gap-2 text-amber-700 dark:text-amber-300 font-semibold mb-1">
                <Clock className="w-4 h-4" />
                Cuenta pendiente de aprobación
              </div>
              <p className="text-xs text-amber-800 dark:text-amber-200 leading-relaxed">
                {pending} Una vez activada, podrás ingresar con el mismo usuario.
              </p>
            </div>
          )}

          {/* Error banner */}
          {error && (
            <div
              data-testid="login-error-banner"
              className="mb-4 rounded-lg border border-red-300 bg-red-50 dark:bg-red-950/30 dark:border-red-800 p-3 text-sm flex items-start gap-2"
            >
              <AlertCircle className="w-4 h-4 mt-0.5 text-red-600 dark:text-red-400 flex-shrink-0" />
              <span className="text-red-800 dark:text-red-200">{error}</span>
            </div>
          )}

          <Tabs value={tab} onValueChange={(v) => { setTab(v); setError(''); }} className="w-full">
            <TabsList className="grid grid-cols-2 w-full mb-5" data-testid="login-method-tabs">
              <TabsTrigger value="email" data-testid="tab-email">Email / Contraseña</TabsTrigger>
              <TabsTrigger value="google" data-testid="tab-google">Google</TabsTrigger>
            </TabsList>

            <TabsContent value="email" className="mt-0">
              <form onSubmit={submit} className="space-y-3" data-testid="email-auth-form">
                {mode === 'register' && (
                  <div>
                    <Label htmlFor="name" className="text-xs">Nombre completo</Label>
                    <Input
                      id="name"
                      data-testid="register-name-input"
                      type="text"
                      value={name}
                      onChange={(e) => setName(e.target.value)}
                      required
                      autoComplete="name"
                      placeholder="Juan Pérez"
                    />
                  </div>
                )}
                <div>
                  <Label htmlFor="email" className="text-xs">Email</Label>
                  <Input
                    id="email"
                    data-testid="email-input"
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    required
                    autoComplete="email"
                    placeholder="tu@email.com"
                  />
                </div>
                <div>
                  <Label htmlFor="password" className="text-xs">Contraseña</Label>
                  <div className="relative">
                    <Input
                      id="password"
                      data-testid="password-input"
                      type={showPwd ? 'text' : 'password'}
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      required
                      autoComplete={mode === 'login' ? 'current-password' : 'new-password'}
                      className="pr-10"
                    />
                    <button
                      type="button"
                      data-testid="toggle-password-visibility"
                      onClick={() => setShowPwd((v) => !v)}
                      className="absolute right-2 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                      tabIndex={-1}
                    >
                      {showPwd ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                    </button>
                  </div>
                  {mode === 'register' && (
                    <p className="text-[11px] text-muted-foreground mt-1">
                      Mínimo 8 caracteres, una mayúscula y un dígito.
                    </p>
                  )}
                </div>

                <Button
                  type="submit"
                  data-testid={mode === 'login' ? 'submit-login-button' : 'submit-register-button'}
                  disabled={loading}
                  className="w-full h-11 mt-2"
                >
                  {loading && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
                  {mode === 'login' ? 'Ingresar' : 'Crear cuenta'}
                </Button>

                <div className="text-center text-xs text-muted-foreground pt-2">
                  {mode === 'login' ? (
                    <>
                      ¿No tenés cuenta?{' '}
                      <button
                        type="button"
                        data-testid="switch-to-register"
                        onClick={() => { setMode('register'); setError(''); setPending(''); }}
                        className="text-blue-600 dark:text-blue-400 hover:underline font-medium"
                      >
                        Crear una
                      </button>
                    </>
                  ) : (
                    <>
                      ¿Ya tenés cuenta?{' '}
                      <button
                        type="button"
                        data-testid="switch-to-login"
                        onClick={() => { setMode('login'); setError(''); }}
                        className="text-blue-600 dark:text-blue-400 hover:underline font-medium"
                      >
                        Ingresar
                      </button>
                    </>
                  )}
                </div>

                {mode === 'register' && (
                  <div className="rounded-md border border-blue-200 bg-blue-50 dark:bg-blue-950/30 dark:border-blue-800 p-3 text-[11px] text-blue-800 dark:text-blue-200 flex items-start gap-2 mt-2">
                    <CheckCircle2 className="w-4 h-4 mt-0.5 flex-shrink-0" />
                    <span>
                      Al crear tu cuenta queda pendiente de aprobación. Un administrador
                      la habilitará antes de que puedas ingresar.
                    </span>
                  </div>
                )}
              </form>
            </TabsContent>

            <TabsContent value="google" className="mt-0">
              <Button
                data-testid="google-login-button"
                onClick={handleGoogleLogin}
                className="w-full h-12 text-base font-medium"
                size="lg"
              >
                <GoogleIcon />
                Continuar con Google
              </Button>
              <p className="text-[11px] text-center text-muted-foreground mt-3 leading-relaxed">
                Si es tu primera vez, tu cuenta quedará pendiente hasta que un administrador
                la habilite.
              </p>
            </TabsContent>
          </Tabs>

          <p className="text-xs text-center text-muted-foreground mt-6">
            Al continuar, aceptas los términos de servicio y la política de privacidad
          </p>
          <p className="text-[10px] text-center text-muted-foreground/70 mt-3 font-mono" data-testid="app-version">
            {appVersion}
          </p>
        </div>
      </motion.div>
    </div>
  );
}

export default Login;
